/**
 * One formula as the reference shows it: name, the equation as KaTeX, a
 * variables table and the notes. Shared by `/library/formulas` and the practice
 * session's "Formler för uppgiften" panel, so both read the same way.
 *
 * `formula.latex` is a raw LaTeX string (not a `{ sv, en }` Markdown field
 * like everything else in `content/schema.ts`), so it is rendered by wrapping
 * it in `$$…$$` and handing it to the shared `<Markdown>` pipeline rather
 * than pulling in a separate KaTeX dependency.
 */
import clsx from "clsx";
import type { FormulaDto } from "@client/api/formulas";
import { Localized, useLocalizedText } from "@client/components/Localized";
import { Markdown } from "@client/components/Markdown";
import { Card, Table } from "@client/components/ui";
import { useT } from "@client/i18n";

export function FormulaCard({ formula, className }: { formula: FormulaDto; className?: string }) {
  const t = useT();
  const localize = useLocalizedText();

  return (
    <Card compact className={clsx("mb-4", className)}>
      <p className="mb-2 font-medium text-ink-800 dark:text-ink-100">{localize(formula.name)}</p>
      <Markdown variant="compact">{`$$${formula.latex}$$`}</Markdown>
      {formula.variables.length > 0 && (
        <Table
          className="mt-3"
          caption={t("content.formulas.variables")}
          rowKey={(variable) => variable.symbol}
          columns={[
            {
              key: "symbol",
              header: t("content.formulas.variableSymbol"),
              cell: (variable) => <Markdown variant="compact">{`$${variable.symbol}$`}</Markdown>,
              className: "w-16 align-top",
            },
            {
              key: "meaning",
              header: t("content.formulas.variableMeaning"),
              // Meanings carry inline math ("laddning $Ze$"), so they are
              // content Markdown like every other content string.
              cell: (variable) => <Localized value={variable.meaning} variant="inline" />,
              className: "text-ink-600 dark:text-ink-400",
            },
          ]}
          rows={formula.variables}
        />
      )}
      {formula.notes && (
        <div className="mt-3 text-sm text-ink-600 dark:text-ink-400">
          <Localized value={formula.notes} variant="compact" />
        </div>
      )}
    </Card>
  );
}
